import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { SupportedNetwork } from 'sacrifice-utils'
import { NetworkTypes } from '@provex/eip712-utils'
import { createPublicClient, http } from 'viem'
import { mainnet, pulsechain } from 'viem/chains'
import { waitForImageLoading } from '../utils/delayed-image-loading'

interface ResolvedName {
  name: string
  chain: SupportedNetwork
  isLoading: boolean
  error?: string
}

interface NameResolutionContextValue {
  // Get resolved names for an address
  getNames: (address: string) => ResolvedName[]
  // Manually resolve names for an address (triggers async lookup)
  resolveNames: (address: string) => Promise<void>
  // Check if names are being loaded for an address
  isLoading: (address: string) => boolean
}

const NameResolutionContext = createContext<NameResolutionContextValue | null>(null)

export const useNameResolution = () => {
  const context = useContext(NameResolutionContext)
  if (!context) {
    throw new Error('useNameResolution must be used within a NameResolutionProvider')
  }
  return context
}

// Lazy-load RPC clients to avoid blocking initial page load
// These are created on-demand when name resolution is actually needed
let ethereumClient: ReturnType<typeof createPublicClient> | null = null
let pulsechainClient: ReturnType<typeof createPublicClient> | null = null

const getEthereumClient = async () => {
  // Defer client creation until after critical path to avoid blocking initial page load
  await waitForImageLoading()

  if (!ethereumClient) {
    ethereumClient = createPublicClient({
      chain: mainnet,
      transport: http(),
    })
  }
  return ethereumClient
}

const getPulsechainClient = async () => {
  // Defer client creation until after critical path to avoid blocking initial page load
  await waitForImageLoading()

  if (!pulsechainClient) {
    pulsechainClient = createPublicClient({
      chain: pulsechain,
      transport: http(),
    })
  }
  return pulsechainClient
}

interface ChainNameResult {
  name: string | null
  error?: string
  loading?: boolean
  resolvedAt?: number // Timestamp when the name was resolved
}

interface NameCache {
  [address: string]: {
    ethereum?: ChainNameResult
    pulsechain?: ChainNameResult
  }
}

// Cache TTL: 1 minute
const CACHE_TTL_MS = 60 * 1000

interface NameResolutionProviderProps {
  children: React.ReactNode
}

export const NameResolutionProvider: React.FC<NameResolutionProviderProps> = ({ children }) => {
  const [nameCache, setNameCache] = useState<NameCache>({})

  const resolveEthereumName = useCallback(async (address: string) => {
    const normalized = address.toLowerCase()

    try {
      setNameCache(prev => ({
        ...prev,
        [normalized]: {
          ...prev[normalized],
          ethereum: { name: null, loading: true },
        },
      }))

      console.log('[NameResolution] Resolving Ethereum ENS for:', normalized)
      const client = await getEthereumClient()
      const ensName = await client.getEnsName({
        address: normalized as `0x${string}`,
      })
      console.log('[NameResolution] Ethereum ENS result:', ensName)

      setNameCache(prev => ({
        ...prev,
        [normalized]: {
          ...prev[normalized],
          ethereum: { name: ensName, loading: false, resolvedAt: Date.now() },
        },
      }))
    } catch (error) {
      console.warn('Failed to resolve Ethereum ENS name:', error)
      setNameCache(prev => ({
        ...prev,
        [normalized]: {
          ...prev[normalized],
          ethereum: {
            name: null,
            error: 'Failed to resolve',
            loading: false,
            resolvedAt: Date.now(),
          },
        },
      }))
    }
  }, [])

  const resolvePulsechainName = useCallback(async (address: string) => {
    const normalized = address.toLowerCase()

    try {
      setNameCache(prev => ({
        ...prev,
        [normalized]: {
          ...prev[normalized],
          pulsechain: { name: null, loading: true },
        },
      }))

      console.log('[NameResolution] Resolving PulseChain PNS for:', normalized)
      const client = await getPulsechainClient()
      const pnsName = await client.getEnsName({
        address: normalized as `0x${string}`,
      })
      console.log('[NameResolution] PulseChain PNS result:', pnsName)

      setNameCache(prev => ({
        ...prev,
        [normalized]: {
          ...prev[normalized],
          pulsechain: { name: pnsName, loading: false, resolvedAt: Date.now() },
        },
      }))
    } catch (error) {
      console.warn('Failed to resolve PulseChain PNS name:', error)
      setNameCache(prev => ({
        ...prev,
        [normalized]: {
          ...prev[normalized],
          pulsechain: {
            name: null,
            error: 'Failed to resolve',
            loading: false,
            resolvedAt: Date.now(),
          },
        },
      }))
    }
  }, [])

  const isCacheExpired = useCallback((result: ChainNameResult | undefined): boolean => {
    if (!result || !result.resolvedAt) {
      return true
    }
    return Date.now() - result.resolvedAt > CACHE_TTL_MS
  }, [])

  const resolveNames = useCallback(
    async (address: string) => {
      const normalized = address.toLowerCase()

      // Use functional setState to read current cache without depending on it
      let shouldResolveEthereum = false
      let shouldResolvePulsechain = false

      setNameCache(prev => {
        const cached = prev[normalized]

        // Don't re-resolve if already loading
        const ethereumLoading = cached?.ethereum?.loading
        const pulsechainLoading = cached?.pulsechain?.loading

        // Check if we need to resolve or refresh (but not if already loading)
        shouldResolveEthereum =
          !ethereumLoading && (!cached?.ethereum || isCacheExpired(cached.ethereum))
        shouldResolvePulsechain =
          !pulsechainLoading && (!cached?.pulsechain || isCacheExpired(cached.pulsechain))

        // Skip if everything is fresh or loading
        if (!shouldResolveEthereum && !shouldResolvePulsechain) {
          console.log('[NameResolution] Cache is fresh or loading for:', address)
          return prev // No change
        }

        // Initialize cache entry if needed
        if (!cached) {
          return {
            ...prev,
            [normalized]: {},
          }
        }

        return prev // No change yet, will be updated by resolve functions
      })

      // Resolve only the expired chains
      const promises: Promise<void>[] = []
      if (shouldResolveEthereum) {
        console.log('[NameResolution] Refreshing Ethereum ENS for:', address)
        promises.push(resolveEthereumName(normalized))
      }
      if (shouldResolvePulsechain) {
        console.log('[NameResolution] Refreshing PulseChain PNS for:', address)
        promises.push(resolvePulsechainName(normalized))
      }

      if (promises.length > 0) {
        await Promise.all(promises)
      }
    },
    [resolveEthereumName, resolvePulsechainName, isCacheExpired]
  )

  const getNames = useCallback(
    (address: string): ResolvedName[] => {
      const normalized = address.toLowerCase()
      const cached = nameCache[normalized]

      if (!cached) {
        return []
      }

      const names: ResolvedName[] = []

      // Ethereum ENS
      if (cached.ethereum) {
        if (cached.ethereum.name) {
          names.push({
            name: cached.ethereum.name,
            chain: { networkType: NetworkTypes.EVM, chainId: mainnet.id },
            isLoading: false,
          })
        } else if (cached.ethereum.loading) {
          names.push({
            name: '',
            chain: { networkType: NetworkTypes.EVM, chainId: mainnet.id },
            isLoading: true,
          })
        }
      }

      // PulseChain PNS
      if (cached.pulsechain) {
        if (cached.pulsechain.name) {
          names.push({
            name: cached.pulsechain.name,
            chain: { networkType: NetworkTypes.EVM, chainId: pulsechain.id },
            isLoading: false,
          })
        } else if (cached.pulsechain.loading) {
          names.push({
            name: '',
            chain: { networkType: NetworkTypes.EVM, chainId: pulsechain.id },
            isLoading: true,
          })
        }
      }

      console.log('[NameResolution] getNames for', address, ':', names)
      return names
    },
    [nameCache]
  )

  const isLoading = useCallback(
    (address: string): boolean => {
      const normalized = address.toLowerCase()
      const cached = nameCache[normalized]
      return Boolean(cached?.ethereum?.loading || cached?.pulsechain?.loading)
    },
    [nameCache]
  )

  const value = useMemo(
    () => ({
      getNames,
      resolveNames,
      isLoading,
    }),
    [getNames, resolveNames, isLoading]
  )

  return <NameResolutionContext.Provider value={value}>{children}</NameResolutionContext.Provider>
}
